/**
 * CurrencyContext — tracks the ACTIVE currency for a signed-in user.
 *
 * Phase-1 model: the app supports 4 currencies (BDT/INR/PKR/USDT). The user
 * has ONE active currency in the UI at a time; bets, balance chips, and
 * deposit/withdraw defaults all follow it. Backend stores balances natively
 * per currency — there is NO conversion.
 *
 * Persistence: `localStorage["bragame_active_currency_v1"]`. On login the
 * default is `user.preferred_currency`. If the user switches to a currency
 * their DB doesn't yet have a bucket for, they still get 0 balance (backend
 * schema is fixed at 4 keys).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useAuth } from "./AuthContext";
import { DEFAULT_CURRENCY, SUPPORTED_CODES, formatAmount, getBalance, getCurrency } from "../lib/currency";

const KEY = "bragame_active_currency_v1";
const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const { user } = useAuth();

  // Local storage seed. On first render (user unknown) we read whatever was
  // last used; when the user object lands we prefer their `preferred_currency`
  // if the local value hasn't been set yet.
  const [activeCurrency, setActiveCurrencyState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_CURRENCY;
    try {
      const stored = localStorage.getItem(KEY);
      if (stored && SUPPORTED_CODES.includes(stored)) return stored;
    } catch { /* localStorage unavailable */ }
    return DEFAULT_CURRENCY;
  });

  // When the user lands (or their preferred_currency changes), snap to it —
  // but only if the local storage key wasn't already set explicitly. This
  // prevents overwriting a manual toggle the user just made.
  useEffect(() => {
    if (!user?.preferred_currency) return;
    let hasStorageKey = false;
    try { hasStorageKey = !!localStorage.getItem(KEY); } catch { /* ignore */ }
    if (!hasStorageKey && SUPPORTED_CODES.includes(user.preferred_currency)) {
      setActiveCurrencyState(user.preferred_currency);
    }
  }, [user?.preferred_currency]);

  useEffect(() => {
    try { localStorage.setItem(KEY, activeCurrency); } catch { /* ignore */ }
  }, [activeCurrency]);

  const setActiveCurrency = useCallback((code) => {
    if (SUPPORTED_CODES.includes(code)) setActiveCurrencyState(code);
  }, []);

  const format = useCallback(
    (amount, opts) => formatAmount(amount, activeCurrency, opts),
    [activeCurrency]
  );

  // Convenience: read the active-currency balance directly.
  const balance = useCallback(
    (mode) => getBalance(user, mode, activeCurrency),
    [user, activeCurrency]
  );

  const value = useMemo(
    () => ({
      activeCurrency,
      setActiveCurrency,
      format,
      balance,
      currencyMeta: getCurrency(activeCurrency),
      // Legacy names kept so old imports keep compiling until Phase-2 wraps.
      currency: activeCurrency,
      setCurrency: setActiveCurrency,
    }),
    [activeCurrency, setActiveCurrency, format, balance]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // Rendered outside the provider — fallback to USDT-native formatting so
    // components don't crash in tests / storybooks.
    return {
      activeCurrency: DEFAULT_CURRENCY,
      currency: DEFAULT_CURRENCY,
      setActiveCurrency: () => {},
      setCurrency: () => {},
      format: (v) => formatAmount(v, DEFAULT_CURRENCY),
      balance: () => 0,
      currencyMeta: getCurrency(DEFAULT_CURRENCY),
    };
  }
  return ctx;
}
